/**
 * Query hook for the Player stats tab. Normalizes raw API-Football snapshots
 * into ranked leaderboard rows (top scorers / assists / yellow / red).
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchLeagueAnalytics } from '../helpers/leagueDetailApi';
import {
  hasAnyLeaderboardData,
  normalizeLeagueAnalytics,
} from '../helpers/mapLeagueAnalytics.helper';
import type { LeagueAnalyticsBoards } from '../types/leagueDetail.types';

const STALE_TIME_MS = 15 * 60 * 1000;

export interface UseLeagueAnalyticsResult {
  boards: LeagueAnalyticsBoards;
  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
}

export function useLeagueAnalytics(
  leagueId: string | undefined,
  enabled = true
): UseLeagueAnalyticsResult {
  const canFetch = enabled && typeof leagueId === 'string' && leagueId.length > 0;
  const query = useQuery({
    queryKey: ['leagueDetail', 'analytics', leagueId],
    queryFn: () => fetchLeagueAnalytics(leagueId ?? ''),
    enabled: canFetch,
    staleTime: STALE_TIME_MS,
  });

  const boards = useMemo(
    () => normalizeLeagueAnalytics(query.data ?? null),
    [query.data]
  );

  return {
    boards,
    isLoading: query.isLoading,
    isError: query.isError,
    isEmpty: !query.isLoading && !hasAnyLeaderboardData(boards),
  };
}
